import DOMPurify from "dompurify";
import React from "react";
import { formatText } from "../../../shared/utils/textFormatting";

interface TextRendererProps {
  text: string;
  allowHtml?: boolean;
  allowedTags?: string[];
  allowedAttr?: string[];
  highlightedQuestionNumber?: number;
}

/**
 * Component that renders text with line breaks and converts image tags to HTML images
 * It handles:
 * 1. Converting \n to <br /> elements
 * 2. Converting [img src="url"] to <img src="url" /> elements
 * 3. Sanitizing and rendering HTML when allowHtml is true
 */
const TextRenderer: React.FC<TextRendererProps> = ({
  text,
  allowHtml = false,
  allowedTags = ["sup", "sub", "b", "i", "em", "strong", "span"],
  allowedAttr = ["class"],
}) => {
  if (!text) return null;

  // Function to process the text and convert it to React elements
  const renderText = () => {
    // If HTML is allowed, process the text with DOMPurify first
    if (allowHtml) {
      // Process the text for image tags first
      const processedText = processImagesInText(text);

      // Then sanitize the HTML
      const sanitizedHtml = DOMPurify.sanitize(processedText, {
        ALLOWED_TAGS: [...allowedTags, "img", "br"],
        ALLOWED_ATTR: [...allowedAttr, "src", "alt", "class"],
      });

      // Return the sanitized HTML
      return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    }

    // If HTML is not allowed, process the text line by line
    // Split the text by newlines to handle line breaks
    const lines = text.split("\n");

    return lines.map((line, lineIndex) => {
      // First apply formatText to handle special formatting
      const formattedText = formatText(line);

      // Check if the line contains an image tag
      const imgRegex = /\[img src="([^"]+)"\]/g;
      let lastIndex = 0;
      const elements: React.ReactNode[] = [];
      let match;

      // If there are no image tags, just return the formatted text
      if (!imgRegex.test(line)) {
        return (
          <React.Fragment key={`line-${lineIndex}`}>
            {formattedText}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Reset regex lastIndex
      imgRegex.lastIndex = 0;

      // Process each image tag in the line
      while ((match = imgRegex.exec(line)) !== null) {
        // Add the text before the image tag
        if (match.index > lastIndex) {
          const textBeforeImg = line.substring(lastIndex, match.index);
          const formattedTextBeforeImg = formatText(textBeforeImg);
          elements.push(formattedTextBeforeImg);
        }

        // Add the image element
        const imgSrc = match[1];
        elements.push(
          <img
            key={`img-${lineIndex}-${match.index}`}
            src={imgSrc}
            alt="Content image"
            className="my-2 max-w-full mx-auto"
          />
        );

        lastIndex = match.index + match[0].length;
      }

      // Add any remaining text after the last image tag
      if (lastIndex < line.length) {
        const textAfterImg = line.substring(lastIndex);
        const formattedTextAfterImg = formatText(textAfterImg);
        elements.push(formattedTextAfterImg);
      }

      // Return the line with images and add a line break if it's not the last line
      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {elements}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  // Process image tags in text and convert them to HTML img tags
  const processImagesInText = (inputText: string): string => {
    const imgRegex = /\[img src="([^"]+)"\]/g;
    return inputText.replace(imgRegex, (match, imgSrc) => {
      return `<img src="${imgSrc}" alt="Content image" class="my-2 max-w-full mx-auto" />`;
    });
  };

  return <div className="text-renderer w-full">{renderText()}</div>;
};

export default TextRenderer;
